<template>
    <div class="page">

        <!-- Row Detail Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>🔔 Notification Log Detail</h3>
                    <button @click="selectedRecord = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">
                        <div v-for="(chunk, ci) in detailChunks" :key="ci" class="detail-section">
                            <div class="detail-grid">
                                <div v-for="col in chunk" :key="col" class="detail-row">
                                    <strong>{{ formatHeader(col) }}:</strong>
                                    <span :class="getDetailCellClass(col, selectedRecord[col])">
                                        {{ formatCell(selectedRecord[col], col) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-bar">
            <div class="header-left">
                <div class="header-icon"><span>🔔</span></div>
                <div>
                    <div class="header-title">IOT · Notification Report Log</div>
                    <div class="header-subtitle">Delivery status across Email, Telegram &amp; WhatsApp</div>
                </div>
            </div>
            <div class="header-right">
                <label class="auto-reload-toggle">
                    <span class="switch">
                        <input type="checkbox" v-model="autoReload" @change="onAutoReloadChange" />
                        <span class="switch-slider"></span>
                    </span>
                    <span>Auto Reload</span>
                </label>
                <div class="live-pill">
                    <span class="live-dot"></span>
                    <span>Live log</span>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- KPI Section -->
            <div class="section-block">
                <div class="section-toolbar-header">
                    <h3 class="section-title">Notification Overview</h3>
                    <button class="ghost-toggle-btn" @click="statsVisible = !statsVisible">
                        {{ statsVisible ? 'Hide' : 'Show' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="kpi-grid">
                        <div class="kpi-card kpi-card--accent">
                            <div class="kpi-label">Total Records</div>
                            <div class="kpi-value">{{ records.length }}</div>
                            <div class="kpi-sub">All log entries</div>
                        </div>
                        <div class="kpi-card">
                            <div class="kpi-label">Filtered</div>
                            <div class="kpi-value">{{ filteredRecords.length }}</div>
                            <div class="kpi-sub">After filters</div>
                        </div>
                        <div class="kpi-card">
                            <div class="kpi-label">Delivered</div>
                            <div class="kpi-value kpi-value--green">{{ deliveredPercent }}%</div>
                            <div class="kpi-sub">{{ deliveredCount }} of {{ filteredRecords.length }}</div>
                        </div>
                        <div class="kpi-card">
                            <div class="kpi-label">Failed</div>
                            <div class="kpi-value kpi-value--red">{{ failedCount }}</div>
                            <div class="kpi-sub">Needs attention</div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters Section -->
            <div class="section-block">
                <div class="section-toolbar-header">
                    <h3 class="section-title">Data Filters</h3>
                    <button class="ghost-toggle-btn" @click="filtersVisible = !filtersVisible">
                        {{ filtersVisible ? 'Hide' : 'Show' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="filter-toolbar">

                        <div class="search-box">
                            <span class="search-icon">🔍</span>
                            <input type="text" v-model="quickSearch" @input="applyFilters" class="search-input"
                                placeholder="Search any field…" />
                        </div>

                        <!-- Date Range Filter -->
                        <div class="date-filter-group">
                            <input type="date" class="date-input" v-model="dateFrom" :max="dateTo"
                                @change="onDateRangeChange" />
                            <span class="date-sep">–</span>
                            <input type="date" class="date-input" v-model="dateTo" :min="dateFrom"
                                @change="onDateRangeChange" />
                        </div>

                        <!-- Notification Type Filter -->
                        <div class="multi-select-container" v-if="uniqueNotificationTypes.length > 0">
                            <div class="multi-select-display filter-chip"
                                :class="{ 'filter-chip--active': selectedFilters.notificationType.length > 0 }"
                                @click="toggleDropdown('notificationType')">
                                <span>Notification type</span>
                                <span class="chip-badge" v-if="selectedFilters.notificationType.length > 0">{{
                                    selectedFilters.notificationType.length }}</span>
                                <span class="chip-caret">▾</span>
                            </div>
                            <div v-if="dropdownStates.notificationType" class="multi-select-dropdown">
                                <div class="select-all-option">
                                    <label class="checkbox-option">
                                        <input type="checkbox" @change="toggleFilterAll('notificationType')"
                                            :checked="selectedFilters.notificationType.length === uniqueNotificationTypes.length" />
                                        <span>Select All</span>
                                    </label>
                                </div>
                                <div class="dropdown-options">
                                    <label class="checkbox-option" v-for="nt in uniqueNotificationTypes" :key="nt">
                                        <input type="checkbox" :value="nt"
                                            v-model="selectedFilters.notificationType" @change="applyFilters" />
                                        <span>{{ nt || '(empty)' }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- IOT Report Type Filter -->
                        <div class="multi-select-container" v-if="uniqueReportTypes.length > 0">
                            <div class="multi-select-display filter-chip"
                                :class="{ 'filter-chip--active': selectedFilters.iotReportType.length > 0 }"
                                @click="toggleDropdown('iotReportType')">
                                <span>Report type</span>
                                <span class="chip-badge" v-if="selectedFilters.iotReportType.length > 0">{{
                                    selectedFilters.iotReportType.length }}</span>
                                <span class="chip-caret">▾</span>
                            </div>
                            <div v-if="dropdownStates.iotReportType" class="multi-select-dropdown">
                                <div class="select-all-option">
                                    <label class="checkbox-option">
                                        <input type="checkbox" @change="toggleFilterAll('iotReportType')"
                                            :checked="selectedFilters.iotReportType.length === uniqueReportTypes.length" />
                                        <span>Select All</span>
                                    </label>
                                </div>
                                <div class="dropdown-options">
                                    <label class="checkbox-option" v-for="rt in uniqueReportTypes" :key="rt">
                                        <input type="checkbox" :value="rt" v-model="selectedFilters.iotReportType"
                                            @change="applyFilters" />
                                        <span>{{ rt || '(empty)' }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Dynamic Column Filters -->
                        <div class="multi-select-container" v-for="filterCol in dynamicFilterColumns" :key="filterCol">
                            <div class="multi-select-display filter-chip"
                                :class="{ 'filter-chip--active': selectedFilters[filterCol] && selectedFilters[filterCol].length > 0 }"
                                @click="toggleDropdown(filterCol)">
                                <span>{{ formatHeader(filterCol) }}</span>
                                <span class="chip-badge"
                                    v-if="selectedFilters[filterCol] && selectedFilters[filterCol].length > 0">{{
                                    selectedFilters[filterCol].length }}</span>
                                <span class="chip-caret">▾</span>
                            </div>
                            <div v-if="dropdownStates[filterCol]" class="multi-select-dropdown">
                                <div class="select-all-option">
                                    <label class="checkbox-option">
                                        <input type="checkbox" @change="toggleFilterAll(filterCol)"
                                            :checked="selectedFilters[filterCol] && selectedFilters[filterCol].length === getUniqueValues(filterCol).length" />
                                        <span>Select All</span>
                                    </label>
                                </div>
                                <div class="dropdown-options">
                                    <label class="checkbox-option" v-for="val in getUniqueValues(filterCol)" :key="val">
                                        <input type="checkbox" :value="val" v-model="selectedFilters[filterCol]"
                                            @change="applyFilters" />
                                        <span>{{ val || '(empty)' }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Visible Columns -->
                        <div class="multi-select-container" v-if="columns.length > 0">
                            <div class="multi-select-display filter-chip" @click="toggleDropdown('columns')">
                                <span>Columns</span>
                                <span class="chip-badge">{{ visibleColumns.length }}/{{ columns.length }}</span>
                                <span class="chip-caret">▾</span>
                            </div>
                            <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                                <div class="select-all-option">
                                    <label class="checkbox-option">
                                        <input type="checkbox" @change="toggleAllColumns"
                                            :checked="visibleColumns.length === columns.length" />
                                        <span>Select All</span>
                                    </label>
                                </div>
                                <div class="dropdown-options">
                                    <label class="checkbox-option" v-for="col in columns" :key="col">
                                        <input type="checkbox" :value="col" v-model="visibleColumns"
                                            @change="onColumnToggle" />
                                        <span>{{ formatHeader(col) }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="reset-chip" @click="resetFilters"><span>↻</span> Reset</div>
                    </div>
                </transition>
            </div>

            <!-- Data Grid -->
            <div class="grid-card">
                <div class="grid-card-header">
                    <div class="grid-title-group">
                        <span class="grid-title">🔔 Notification entries</span>
                        <span v-if="!loading && records.length > 0" class="records-pill">
                            {{ filteredRecords.length }} records
                        </span>
                    </div>
                    <div class="grid-actions">
                        <button v-if="hasActiveColumnSearches" class="clear-search-btn" @click="clearColumnSearches">
                            ✖ Clear Searches
                        </button>
                        <div class="rows-selector">
                            <span>Show</span>
                            <select v-model.number="itemsPerPage" @change="currentPage = 1" class="rows-select">
                                <option :value="10">10 rows</option>
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                                <option :value="200">200 rows</option>
                            </select>
                        </div>
                        <button class="btn-outline" @click="printReport">🖨️ Print</button>
                        <button class="btn-outline btn-outline--orange" @click="loadData">🔄 Refresh</button>
                        <button class="btn-solid-orange" @click="exportCSV">📥 Export</button>
                    </div>
                </div>

                <div class="table-container">
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>

                    <div v-if="!loading && records.length === 0" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Data Available</div>
                        <div class="empty-description">Notification logs will appear here as they are generated.
                        </div>
                    </div>

                    <table v-if="!loading && records.length > 0" class="data-table">
                        <thead>
                            <tr class="head-row">
                                <th v-for="col in visibleColumns" :key="col" @click="sortBy(col)"
                                    :class="{ 'th-sorted': sort.col === col }">
                                    {{ formatHeader(col) }}
                                    <span class="sort-glyph">
                                        {{ sort.col === col ? (sort.asc ? '▲' : '▼') : '⇅' }}
                                    </span>
                                </th>
                                <th class="action-th">Manual Process</th>
                            </tr>
                            <tr class="search-row">
                                <th v-for="col in visibleColumns" :key="'s-' + col" class="search-cell">
                                    <input type="text" class="column-search-input"
                                        :placeholder="'Filter ' + formatHeader(col).toLowerCase() + '…'"
                                        v-model="colSearch[col]" @input="handleColumnSearch" />
                                </th>
                                <th class="search-cell"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="paginatedRecords.length === 0">
                                <td :colspan="visibleColumns.length + 1" class="empty-state"
                                    style="padding:40px;text-align:center;">
                                    <div>No matching records — try clearing the filters.</div>
                                </td>
                            </tr>
                            <tr v-for="(rec, idx) in paginatedRecords" :key="idx" :class="getRowClass(rec)"
                                @click="selectedRecord = rec">
                                <td v-for="col in visibleColumns" :key="col">
                                    <!-- Channel / ID -->
                                    <div v-if="col === 'chatbotID'" class="cell-flex">
                                        <span class="pill" :class="channelClass(rec[col])">{{ channelName(rec[col])
                                            }}</span>
                                        <span class="id-inline">#{{ rec.id != null ? rec.id : '—' }}</span>
                                    </div>
                                    <!-- Notification type badge -->
                                    <span v-else-if="col === 'notificationType' && rec[col]" class="pill"
                                        :class="hashPillClass(rec[col])">
                                        {{ rec[col] }}
                                    </span>
                                    <!-- Status (derived from error) -->
                                    <span v-else-if="col === 'error'" class="status-pill"
                                        :class="isFailed(rec) ? 'status-failed' : 'status-delivered'"
                                        :title="isFailed(rec) ? rec.error : ''">
                                        <span class="dot"></span>{{ isFailed(rec) ? 'Failed' : 'Delivered' }}
                                    </span>
                                    <!-- Body/Message fields (with recipient sub-line) -->
                                    <div v-else-if="(col === 'body' || col === 'notificationMessage' || col === 'customMessage') && rec[col]">
                                        <div class="cell-primary" :title="rec[col]">{{ truncate(rec[col], 60) }}</div>
                                        <div v-if="rec.numbers" class="cell-secondary">{{ rec.numbers }}</div>
                                    </div>
                                    <!-- Created date/time -->
                                    <div v-else-if="col === 'createDate' && rec[col]">
                                        <div class="date-line">{{ splitDateTime(rec[col]).date }}</div>
                                        <div class="time-line">{{ splitDateTime(rec[col]).time }}</div>
                                    </div>
                                    <!-- ID columns -->
                                    <code v-else-if="isIdCol(col)"
                                        class="id-code">{{ rec[col] != null ? rec[col] : '—' }}</code>
                                    <!-- Default -->
                                    <span v-else>{{ formatCell(rec[col], col) }}</span>
                                </td>
                                <td class="action-cell" @click.stop>
                                    <button class="btn-resend" :disabled="resendingIds.includes(rec.id)"
                                        @click="resendNotification(rec)">
                                        {{ resendingIds.includes(rec.id) ? 'Processing…' : '🔁 Process' }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="totalPages > 1" class="pagination-footer">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, filteredRecords.length) }}
                        of {{ filteredRecords.length }} entries
                    </div>
                    <div class="pager">
                        <button class="page-btn-1c" @click="currentPage = 1" :disabled="currentPage === 1">First</button>
                        <button class="page-btn-1c" @click="currentPage--" :disabled="currentPage === 1">Prev</button>
                        <button v-for="p in visiblePageNumbers" :key="p" class="page-btn-1c"
                            :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
                        <button class="page-btn-1c" @click="currentPage++"
                            :disabled="currentPage === totalPages">Next</button>
                        <button class="page-btn-1c" @click="currentPage = totalPages"
                            :disabled="currentPage === totalPages">Last</button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTNotificationReportLog',

    data: function () {
        return {
            loading: false,
            records: [],
            columns: [],
            visibleColumns: [],
            colSearch: {},
            quickSearch: '',
            filtersVisible: true,
            statsVisible: true,
            dropdownStates: {
                columns: false,
                notificationType: false,
                iotReportType: false
            },
            _handleClickOutsideBound: null,
            currentPage: 1,
            itemsPerPage: 25,
            sort: { col: null, asc: true },
            selectedRecord: null,
            selectedFilters: {
                notificationType: [],
                iotReportType: []
            },
            uniqueNotificationTypes: [],
            uniqueReportTypes: [],
            dynamicFilterColumns: [],
            resendUrl: 'https://fams24function.azurewebsites.net/api/ResendNotification',
            resendingIds: [],
            dateFrom: '',
            dateTo: '',
            autoReload: false,
            autoReloadTimer: null
        };
    },

    mounted: function () {
        var self = this;
        this._handleClickOutsideBound = function (e) { self.handleClickOutside(e); };
        document.addEventListener('click', this._handleClickOutsideBound);
        this.dateFrom = this.getMonthStart();
        this.dateTo = this.getMonthEnd();
        this.loadData();
    },

    beforeDestroy: function () {
        if (this._handleClickOutsideBound) document.removeEventListener('click', this._handleClickOutsideBound);
        if (this.autoReloadTimer) clearInterval(this.autoReloadTimer);
    },

    computed: {
        hasActiveColumnSearches: function () {
            var self = this;
            return this.columns.some(function (c) { return self.colSearch[c]; });
        },

        filteredRecords: function () {
            var self = this;
            var result = this.records.slice();

            if (this.quickSearch) {
                var qs = this.quickSearch.toLowerCase();
                result = result.filter(function (r) {
                    return self.columns.some(function (c) {
                        var v = r[c];
                        return v != null && String(v).toLowerCase().includes(qs);
                    });
                });
            }

            this.columns.forEach(function (col) {
                var term = (self.colSearch[col] || '').toLowerCase();
                if (!term) return;
                result = result.filter(function (r) {
                    var v = r[col];
                    return v != null && String(v).toLowerCase().includes(term);
                });
            });

            if (this.selectedFilters.notificationType.length > 0) {
                result = result.filter(function (r) {
                    return self.selectedFilters.notificationType.includes(r.notificationType);
                });
            }

            if (this.selectedFilters.iotReportType.length > 0) {
                result = result.filter(function (r) {
                    return self.selectedFilters.iotReportType.includes(r.iotReportTypeID);
                });
            }

            // Dynamic column filters
            self.dynamicFilterColumns.forEach(function (col) {
                if (self.selectedFilters[col] && self.selectedFilters[col].length > 0) {
                    result = result.filter(function (r) {
                        return self.selectedFilters[col].includes(r[col]);
                    });
                }
            });

            if (this.sort.col) {
                var sc = this.sort.col, asc = this.sort.asc;
                result = result.slice().sort(function (a, b) {
                    var av = a[sc] != null ? a[sc] : '', bv = b[sc] != null ? b[sc] : '';
                    var na = parseFloat(av), nb = parseFloat(bv);
                    if (!isNaN(na) && !isNaN(nb)) return asc ? na - nb : nb - na;
                    return asc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
                });
            }

            return result;
        },

        totalPages: function () { return Math.ceil(this.filteredRecords.length / this.itemsPerPage); },

        paginatedRecords: function () {
            var s = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredRecords.slice(s, s + this.itemsPerPage);
        },

        visiblePageNumbers: function () {
            var maxV = 5, pages = [], start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        detailChunks: function () {
            if (!this.selectedRecord) return [];
            var cols = this.columns, cs = Math.ceil(cols.length / 2), result = [];
            for (var i = 0; i < cols.length; i += cs) result.push(cols.slice(i, i + cs));
            return result;
        },

        deliveredCount: function () {
            var self = this;
            return this.filteredRecords.filter(function (r) { return !self.isFailed(r); }).length;
        },

        deliveredPercent: function () {
            if (!this.filteredRecords.length) return 0;
            return Math.round((this.deliveredCount / this.filteredRecords.length) * 1000) / 10;
        },

        failedCount: function () {
            return this.filteredRecords.length - this.deliveredCount;
        }
    },

    methods: {
        loadData: function () {
            var self = this;
            self.loading = true;
            self.records = [];
            self.columns = [];
            self.visibleColumns = [];
            self.colSearch = {};
            self.currentPage = 1;

            var url = 'IOTReporting/GetIotReportLog';
            var params = [];
            if (self.dateFrom) params.push('startDate=' + encodeURIComponent(self.dateFrom));
            if (self.dateTo) params.push('endDate=' + encodeURIComponent(self.dateTo));
            if (params.length) url += '?' + params.join('&');

            var onSuccess = function (response) {
                var data = response.data && response.data.data ? response.data.data : response.data;
                if (!data || !data.length) {
                    self.loading = false;
                    return;
                }
                self.columns = Object.keys(data[0]);
                self.visibleColumns = self.columns.slice();
                self.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
                self.records = data;
                self.extractUniqueFilterValues();
                self.loading = false;
            };

            var onError = function () { self.loading = false; };

            self.$ajaxGet(self, url, onSuccess, onError);
        },

        extractUniqueFilterValues: function () {
            var unique = [];
            this.records.forEach(function (r) {
                var v = r.notificationType;
                if (v != null && v !== '' && !unique.includes(v)) unique.push(v);
            });
            this.uniqueNotificationTypes = unique.sort();

            var uniqueRt = [];
            this.records.forEach(function (r) {
                var v = r.iotReportTypeID;
                if (v != null && !uniqueRt.includes(v)) uniqueRt.push(v);
            });
            this.uniqueReportTypes = uniqueRt.sort(function (a, b) { return a - b; });

            // Determine which columns to use as filters (exclude ID columns, message columns, timestamps, and specific fields)
            var self = this;
            var excludePatterns = ['id', 'message', 'body', 'error', 'timestamp', 'date', 'time', 'notificationtype', 'iotreporttypeid'];
            var filterColumns = this.columns.filter(function (col) {
                var lc = col.toLowerCase();
                var isExcluded = excludePatterns.some(function (pattern) {
                    return lc.includes(pattern);
                });
                return !isExcluded;
            });

            this.dynamicFilterColumns = filterColumns.slice(0, 3); // Limit to 3 dynamic filters to avoid clutter

            // Initialize dynamic filters
            this.dynamicFilterColumns.forEach(function (col) {
                self.selectedFilters[col] = [];
                self.$set(self.dropdownStates, col, false);
            });

            this.selectedFilters = Object.assign({}, this.selectedFilters, {
                notificationType: [],
                iotReportType: []
            });
        },

        getUniqueValues: function (col) {
            var unique = [];
            this.records.forEach(function (r) {
                var v = r[col];
                if (v != null && !unique.includes(v)) unique.push(v);
            });
            return unique.sort(function (a, b) {
                var aNum = parseFloat(a), bNum = parseFloat(b);
                if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                return String(a).localeCompare(String(b));
            });
        },

        applyFilters: function () { this.currentPage = 1; },
        handleColumnSearch: function () { this.currentPage = 1; },

        clearColumnSearches: function () {
            var self = this;
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
            this.currentPage = 1;
        },

        resetFilters: function () {
            var self = this;
            this.quickSearch = '';
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });

            var newFilters = { notificationType: [], iotReportType: [] };
            this.dynamicFilterColumns.forEach(function (col) {
                newFilters[col] = [];
            });
            this.selectedFilters = newFilters;

            this.currentPage = 1;
            this.sort = { col: null, asc: true };
            this.visibleColumns = this.columns.slice();

            this.dateFrom = this.getMonthStart();
            this.dateTo = this.getMonthEnd();
            this.loadData();
        },

        getMonthStart: function () {
            var d = new Date();
            return this.formatDateInput(new Date(d.getFullYear(), d.getMonth(), 1));
        },

        getMonthEnd: function () {
            var d = new Date();
            return this.formatDateInput(new Date(d.getFullYear(), d.getMonth() + 1, 0));
        },

        formatDateInput: function (date) {
            var y = date.getFullYear();
            var m = String(date.getMonth() + 1).padStart(2, '0');
            var d = String(date.getDate()).padStart(2, '0');
            return y + '-' + m + '-' + d;
        },

        onDateRangeChange: function () {
            if (this.dateFrom && this.dateTo && this.dateTo < this.dateFrom) {
                this.dateTo = this.dateFrom;
            }
            this.currentPage = 1;
            this.loadData();
        },

        onAutoReloadChange: function () {
            var self = this;
            if (this.autoReloadTimer) {
                clearInterval(this.autoReloadTimer);
                this.autoReloadTimer = null;
            }
            if (this.autoReload) {
                this.autoReloadTimer = setInterval(function () { self.loadData(); }, 30000);
            }
        },

        toggleDropdown: function (key) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) {
                self.dropdownStates[k] = k === key ? !self.dropdownStates[k] : false;
            });
        },

        toggleFilterAll: function (filterKey) {
            var filterArray = this.selectedFilters[filterKey];
            var uniqueArray = this.getUniqueValues(filterKey);

            if (filterArray && filterArray.length === uniqueArray.length) {
                this.selectedFilters[filterKey] = [];
            } else {
                this.selectedFilters[filterKey] = uniqueArray.slice();
            }
            this.applyFilters();
        },

        handleClickOutside: function (e) {
            var self = this;
            var hasClass = function (el, cls) { return el && el.classList && el.classList.contains(cls); };
            if (!hasClass(e.target, 'multi-select-display') && !e.target.closest('.multi-select-container')) {
                Object.keys(self.dropdownStates).forEach(function (k) { self.dropdownStates[k] = false; });
            }
        },

        toggleAllColumns: function () {
            if (this.visibleColumns.length === this.columns.length) {
                this.visibleColumns = [this.columns[0]];
            } else {
                this.visibleColumns = this.columns.slice();
            }
            this.currentPage = 1;
        },

        onColumnToggle: function () {
            if (!this.visibleColumns.length) this.visibleColumns = [this.columns[0]];
            this.currentPage = 1;
        },

        sortBy: function (col) {
            if (this.sort.col === col) this.sort.asc = !this.sort.asc;
            else { this.sort.col = col; this.sort.asc = true; }
            this.currentPage = 1;
        },

        formatHeader: function (col) {
            var overrides = {
                chatbotID: 'Channel / ID',
                createDate: 'Created',
                error: 'Status',
                numbers: 'Recipient'
            };
            if (overrides[col]) return overrides[col];
            return col.replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        },

        formatCell: function (value, col) {
            if (value == null || value === '') return '—';
            return value;
        },

        truncate: function (value, len) {
            if (!value) return '—';
            var s = String(value);
            return s.length > len ? s.substring(0, len) + '…' : s;
        },

        isIdCol: function (col) {
            var lc = col.toLowerCase();
            return lc === 'id' || lc.endsWith('id') || lc.includes('accountid');
        },

        isFailedValue: function (value) {
            if (value == null) return false;
            var s = String(value).trim();
            return s !== '' && s.toUpperCase() !== 'N/A';
        },

        isFailed: function (rec) {
            return this.isFailedValue(rec && rec.error);
        },

        channelName: function (chatbotId) {
            var names = { 3: 'Telegram', 4: 'Email', 5: 'WhatsApp' };
            return names[chatbotId] || ('Channel ' + (chatbotId != null ? chatbotId : '—'));
        },

        channelClass: function (chatbotId) {
            var classes = { 3: 'pill-indigo', 4: 'pill-violet', 5: 'pill-green' };
            return classes[chatbotId] || 'pill-slate';
        },

        hashPillClass: function (value) {
            var classes = ['pill-indigo', 'pill-green', 'pill-orange', 'pill-violet'];
            var s = String(value || '');
            var hash = 0;
            for (var i = 0; i < s.length; i++) { hash = (hash * 31 + s.charCodeAt(i)) | 0; }
            return classes[Math.abs(hash) % classes.length];
        },

        splitDateTime: function (value) {
            if (!value) return { date: '—', time: '' };
            var parts = String(value).split(' ');
            return { date: parts[0] || '—', time: parts[1] || '' };
        },

        getRowClass: function (rec) {
            return this.isFailed(rec) ? 'row-failed' : '';
        },

        getDetailCellClass: function (col, value) {
            if (col === 'error' && this.isFailedValue(value)) return 'error-text';
            return '';
        },

        exportCSV: function () {
            if (!this.filteredRecords.length) { alert('No data to export.'); return; }
            var cols = this.visibleColumns;
            var rows = [cols.map(function (c) { return '"' + c + '"'; }).join(',')];
            this.filteredRecords.forEach(function (r) {
                rows.push(cols.map(function (c) {
                    var v = r[c] != null ? r[c] : '';
                    return '"' + String(v).replace(/"/g, '""') + '"';
                }).join(','));
            });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }));
            a.download = 'IOT_Notification_Log.csv';
            a.click();
        },

        printReport: function () { window.print(); },

        resendNotification: function (rec) {
            var self = this;
            if (!rec || rec.id == null || this.resendingIds.includes(rec.id)) return;
            this.resendingIds.push(rec.id);

            this.$http.post(this.resendUrl, { Id: rec.id })
                .then(function (response) {
                    var data = response.data || {};
                    self.$vs.notify({
                        time: 4500,
                        title: 'Manual Process',
                        text: data.message || ('Notification ' + rec.id + ' resent successfully'),
                        color: 'success',
                        iconPack: 'feather',
                        icon: 'icon-check-circle'
                    });
                    self.loadData();
                })
                .catch(function (error) {
                    var data = error.response && error.response.data;
                    var text = 'Resend failed';
                    if (typeof data === 'string' && data) text = data;
                    else if (data && data.message) text = data.message;
                    else if (error.message) text = error.message;

                    self.$vs.notify({
                        time: 6500,
                        title: 'Manual Process Failed',
                        text: String(text),
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle'
                    });
                    self.loadData();
                })
                .finally(function () {
                    self.resendingIds = self.resendingIds.filter(function (id) { return id !== rec.id; });
                });
        }
    }
};
</script>

<style scoped>
* {
    box-sizing: border-box;
}

.page {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #f1f5f9;
    min-height: 100vh;
}

/* Header */
.header-bar {
    background: linear-gradient(120deg, #9a3412 0%, #ea580c 55%, #f59e0b 120%);
    padding: 18px 26px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.header-icon {
    width: 46px;
    height: 46px;
    background: rgba(255, 255, 255, .18);
    border: 1px solid rgba(255, 255, 255, .28);
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 23px;
}

.header-title {
    color: #fff;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -.01em;
}

.header-subtitle {
    color: rgba(255, 255, 255, .82);
    font-size: 13px;
    font-weight: 500;
    margin-top: 3px;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 14px;
}

.auto-reload-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
}

.switch {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    flex-shrink: 0;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.switch-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: rgba(255, 255, 255, .3);
    border: 1px solid rgba(255, 255, 255, .4);
    transition: .2s;
    border-radius: 999px;
}

.switch-slider::before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 2px;
    top: 2px;
    background: #fff;
    transition: .2s;
    border-radius: 50%;
}

.switch input:checked+.switch-slider {
    background: #4ade80;
    border-color: #4ade80;
}

.switch input:checked+.switch-slider::before {
    transform: translateX(16px);
}

.live-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .16);
    border: 1px solid rgba(255, 255, 255, .24);
    padding: 8px 16px;
    border-radius: 999px;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
}

.live-dot {
    width: 9px;
    height: 9px;
    background: #4ade80;
    border-radius: 50%;
    display: inline-block;
    animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
    0% {
        box-shadow: 0 0 0 0 rgba(74, 222, 128, .6);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(74, 222, 128, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(74, 222, 128, 0);
    }
}

/* Main */
.main-content {
    padding: 24px 26px 28px;
}

.section-block {
    margin-bottom: 20px;
}

.section-toolbar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.section-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
}

.ghost-toggle-btn {
    background: none;
    border: none;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 8px;
}

.ghost-toggle-btn:hover {
    color: #c2410c;
}

/* KPI cards */
.kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.kpi-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 18px 20px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .05);
}

.kpi-card--accent {
    border-top: 3px solid #ea580c;
}

.kpi-label {
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: #64748b;
}

.kpi-value {
    font-size: 30px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
    margin-top: 6px;
}

.kpi-value--green {
    color: #16a34a;
}

.kpi-value--red {
    color: #dc2626;
}

.kpi-sub {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 6px;
}

/* Filter toolbar */
.filter-toolbar {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .05);
}

.search-box {
    position: relative;
    flex: 1;
    min-width: 260px;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    color: #94a3b8;
}

.search-input {
    width: 100%;
    padding: 11px 13px 11px 36px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    color: #334155;
    font-family: inherit;
    background: #f8fafc;
}

.search-input:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 3px rgba(234, 88, 12, .1);
}

.date-filter-group {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 9px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
}

.date-input {
    border: none;
    outline: none;
    font-size: 13px;
    color: #334155;
    font-family: inherit;
    background: transparent;
    padding: 2px 0;
}

.date-sep {
    color: #94a3b8;
    font-size: 12px;
}

.multi-select-container {
    position: relative;
}

.filter-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 11px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    font-size: 13.5px;
    color: #475569;
    font-weight: 500;
    cursor: pointer;
    transition: border-color .2s;
    white-space: nowrap;
}

.filter-chip:hover {
    border-color: #ea580c;
}

.filter-chip--active {
    border: 1px solid #fed7aa;
    background: #fff7ed;
    color: #c2410c;
    font-weight: 600;
}

.chip-badge {
    background: #ea580c;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 0 7px;
    border-radius: 999px;
    line-height: 1.5;
}

.chip-caret {
    color: #94a3b8;
    font-size: 10px;
}

.reset-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 11px 14px;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
    cursor: pointer;
}

.reset-chip:hover {
    color: #c2410c;
}

.multi-select-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 220px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
    z-index: 1000;
    max-height: 220px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.columns-dropdown {
    min-width: 240px;
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
}

.dropdown-options {
    overflow-y: auto;
    flex: 1;
    padding: 6px 0;
}

.checkbox-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 13px;
    color: #374151;
    transition: background .15s;
}

.checkbox-option:hover {
    background: #f8fafc;
}

.checkbox-option input[type="checkbox"] {
    accent-color: #ea580c;
}

/* Grid card */
.grid-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(15, 23, 42, .06);
}

.grid-card-header {
    padding: 16px 22px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.grid-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.grid-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
}

.records-pill {
    font-size: 12.5px;
    font-weight: 600;
    background: #fff7ed;
    color: #c2410c;
    border: 1px solid #fed7aa;
    padding: 3px 11px;
    border-radius: 999px;
}

.grid-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.clear-search-btn {
    padding: 6px 12px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    cursor: pointer;
    font-size: 12.5px;
    color: #c2410c;
    font-weight: 500;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #64748b;
}

.rows-select {
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 13px;
    color: #475569;
    background: #fff;
    cursor: pointer;
}

.btn-outline,
.btn-outline--orange,
.btn-solid-orange {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 14px;
    border-radius: 9px;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    transition: all .2s;
}

.btn-outline:hover {
    background: #f8fafc;
}

.btn-outline--orange {
    border-color: #fdba74;
    color: #c2410c;
    font-weight: 600;
}

.btn-outline--orange:hover {
    background: #fff7ed;
}

.btn-solid-orange {
    border: none;
    padding: 9px 16px;
    color: #fff;
    font-weight: 600;
    background: linear-gradient(120deg, #ea580c, #f59e0b);
}

.btn-solid-orange:hover {
    opacity: .92;
}

.table-container {
    overflow-x: auto;
    max-height: 640px;
    min-height: 220px;
    overflow-y: auto;
    position: relative;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, .8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.loader {
    width: 36px;
    height: 36px;
    border: 3px solid #e2e8f0;
    border-top-color: #ea580c;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #94a3b8;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 12px;
}

.empty-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
}

.empty-description {
    font-size: .9rem;
}

/* Table */
.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}

.head-row {
    background: #0f172a;
}

.data-table th {
    padding: 15px 22px;
    text-align: left;
    color: #e2e8f0;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
}

.th-sorted {
    color: #fdba74 !important;
    border-bottom: 2px solid #f59e0b;
}

.action-th {
    cursor: default;
    text-align: center;
}

.sort-glyph {
    margin-left: 4px;
    font-size: 11px;
    opacity: .8;
}

.search-row {
    background: #f8fafc;
}

.search-cell {
    padding: 8px 22px !important;
    border-bottom: 1px solid #eef2f6;
}

.column-search-input {
    width: 100%;
    padding: 7px 11px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 12.5px;
    font-family: inherit;
    color: #334155;
    background: #fff;
}

.column-search-input:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, .1);
}

.data-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    background: #fff;
    cursor: pointer;
    transition: background .15s;
}

.data-table tbody tr:hover {
    background: #fff7ed;
}

.data-table td {
    padding: 16px 22px;
    color: #334155;
    white-space: nowrap;
    vertical-align: middle;
}

/* Row states */
.row-failed {
    background: #fef6f6 !important;
}

.row-failed:hover {
    background: #fde8e8 !important;
}

/* Combined channel/id cell */
.cell-flex {
    display: flex;
    align-items: center;
    gap: 10px;
}

.id-inline {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12.5px;
    color: #94a3b8;
}

/* Pills */
.pill {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 600;
}

.pill-indigo {
    background: #eef2ff;
    color: #4338ca;
}

.pill-green {
    background: #ecfdf5;
    color: #047857;
}

.pill-orange {
    background: #fff7ed;
    color: #c2410c;
}

.pill-violet {
    background: #f5f3ff;
    color: #6d28d9;
}

.pill-slate {
    background: #f1f5f9;
    color: #475569;
}

/* Status pill */
.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 13px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
}

.status-pill .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    display: inline-block;
}

.status-delivered {
    background: #ecfdf5;
    color: #047857;
}

.status-delivered .dot {
    background: #10b981;
}

.status-failed {
    background: #fef2f2;
    color: #b91c1c;
}

.status-failed .dot {
    background: #ef4444;
}

/* Message / recipient cell */
.cell-primary {
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 360px;
}

.cell-secondary {
    color: #94a3b8;
    font-size: 12.5px;
    margin-top: 3px;
}

/* Created date/time cell */
.date-line {
    font-weight: 500;
    color: #475569;
}

.time-line {
    font-size: 12.5px;
    color: #94a3b8;
    margin-top: 2px;
}

.id-code {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12.5px;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 5px;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.error-text {
    color: #dc2626;
}

/* Manual process action column */
.action-cell {
    text-align: center;
    white-space: nowrap;
}

.btn-resend {
    padding: 7px 13px;
    border: 1px solid #fdba74;
    border-radius: 8px;
    background: #fff7ed;
    color: #c2410c;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s;
}

.btn-resend:hover:not(:disabled) {
    background: #fed7aa;
}

.btn-resend:disabled {
    opacity: .6;
    cursor: not-allowed;
}

/* Pagination */
.pagination-footer {
    padding: 16px 22px;
    border-top: 1px solid #eef2f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fafbfc;
    flex-wrap: wrap;
    gap: 10px;
}

.pagination-info {
    font-size: 13px;
    color: #64748b;
}

.pager {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.page-btn-1c {
    padding: 8px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    color: #475569;
    font-weight: 500;
    transition: all .2s;
}

.page-btn-1c:hover:not(:disabled) {
    border-color: #ea580c;
    color: #c2410c;
}

.page-btn-1c:disabled {
    color: #94a3b8;
    cursor: not-allowed;
}

.page-btn-1c.active {
    background: linear-gradient(120deg, #ea580c, #f59e0b);
    color: #fff;
    border-color: transparent;
    font-weight: 700;
}

/* Popup */
.simple-popup {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .5);
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.popup-content {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

.wide-popup {
    width: 900px;
    max-width: 95vw;
}

.popup-header {
    padding: 16px 20px;
    background: linear-gradient(120deg, #9a3412 0%, #ea580c 55%, #f59e0b 120%);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.popup-header h3 {
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .4);
}

.popup-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.detail-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 14px;
    border: 1px solid #e2e8f0;
}

.detail-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-row {
    font-size: .875rem;
    color: #334155;
    padding: 4px 0;
    border-bottom: 1px solid #f1f5f9;
}

.detail-row strong {
    color: #1e293b;
    margin-right: 4px;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

@media (max-width: 768px) {
    .main-content {
        padding: 12px;
    }

    .kpi-grid {
        grid-template-columns: 1fr 1fr;
    }

    .filter-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .detail-sections {
        grid-template-columns: 1fr;
    }

    .data-table {
        font-size: .75rem;
    }

    .data-table th,
    .data-table td {
        padding: 8px;
    }
}

@media print {

    .header-bar,
    .section-block,
    .pagination-footer,
    .simple-popup,
    .action-th,
    .action-cell {
        display: none !important;
    }

    .grid-card {
        box-shadow: none;
        border: none;
    }

    .table-container {
        max-height: none !important;
        overflow: visible !important;
    }

    .head-row {
        background: #ccc !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}
</style>
